/**
 * AdminMadrassaRequests — Page Admin global pour valider/rejeter les
 * demandes de création de madrassa soumises au signup (rôle "madrassa").
 *
 * Route: /admin/madrassa-requests (admin-only)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, XCircle, RefreshCcw, Mail, MapPin, Phone, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CLS = {
  pending:  'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};

export const AdminMadrassaRequestsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, statusFilter]);

  const refresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('noor_albayan_token');
      const r = await axios.get(`${API_URL}/api/onboarding/admin/madrassa-requests`, {
        params: { status: statusFilter },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setItems(r.data || []);
    } catch (e) {
      setItems([]);
    } finally { setLoading(false); }
  };

  const approve = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const token = localStorage.getItem('noor_albayan_token');
      await axios.post(`${API_URL}/api/onboarding/admin/madrassa-requests/${id}/approve`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      await refresh();
    } finally { setBusyId(null); }
  };

  const submitReject = async (id) => {
    if (busyId) return;
    setBusyId(id);
    try {
      const token = localStorage.getItem('noor_albayan_token');
      await axios.post(`${API_URL}/api/onboarding/admin/madrassa-requests/${id}/reject`,
        { reason: reason.trim() || null },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setRejectingId(null); setReason('');
      await refresh();
    } finally { setBusyId(null); }
  };

  if (authLoading || loading) return <PageLoading />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28" data-testid="admin-madrassa-requests-page">
      <Link to="/admin/madrassas"
            className="inline-flex items-center text-text-secondary hover:text-text-primary transition-colors mb-6 text-sm">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux madrassas
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <SectionHeader title="Demandes de création de madrassa"
                       subtitle="Validez ou rejetez les demandes soumises lors de l'inscription." />
        <Button variant="outline" size="sm" onClick={refresh} data-testid="requests-refresh-btn">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
      </div>

      <div className="flex gap-2 mb-6" data-testid="requests-status-filter">
        {[
          { id: 'pending',  label: 'En attente' },
          { id: 'approved', label: 'Approuvées' },
          { id: 'rejected', label: 'Rejetées' },
          { id: 'all',      label: 'Toutes' },
        ].map((f) => (
          <button key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  data-testid={`requests-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    statusFilter === f.id
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-surface/40 border-border text-text-secondary hover:border-primary/40'
                  }`}>
            {f.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <GlassCard className="text-center py-12">
          <p className="text-text-secondary">Aucune demande dans cette catégorie.</p>
        </GlassCard>
      ) : (
        <div className="space-y-4" data-testid="requests-list">
          {items.map((req) => (
            <GlassCard key={req.id} className="p-5" data-testid={`request-row-${req.id}`}>
              <div className="flex flex-wrap justify-between gap-4">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-serif text-text-primary">{req.madrassa_name}</h3>
                    <Badge className={`${STATUS_CLS[req.status]} border text-[10px]`}>
                      {req.status === 'pending' ? 'En attente' : req.status === 'approved' ? 'Approuvée' : 'Rejetée'}
                    </Badge>
                  </div>
                  <div className="text-xs text-text-muted space-y-1">
                    <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {req.user_name} — <span className="text-text-secondary">{req.user_email}</span></div>
                    <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {req.city}, {req.country}</div>
                    {req.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {req.phone}</div>}
                    <div className="text-text-muted">Soumise le {new Date(req.created_at).toLocaleString('fr-FR')}</div>
                  </div>
                  {req.decision_reason && (
                    <div className="mt-2 text-xs text-rose-300/80 italic">Motif : {req.decision_reason}</div>
                  )}
                </div>

                {req.status === 'pending' && (
                  <div className="flex flex-col gap-2 self-start">
                    <Button size="sm" onClick={() => approve(req.id)}
                            disabled={busyId === req.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`request-approve-${req.id}`}>
                      {busyId === req.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                      Approuver
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => { setRejectingId(req.id); setReason(''); }}
                            disabled={busyId === req.id}
                            data-testid={`request-reject-${req.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                    </Button>
                  </div>
                )}
              </div>

              {rejectingId === req.id && (
                <div className="mt-4 p-3 rounded-lg bg-rose-500/5 border border-rose-500/20 space-y-2">
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                            placeholder="Motif du refus (optionnel — communiqué à l'utilisateur par email)"
                            className="bg-surface border-border text-sm"
                            data-testid={`request-reject-reason-${req.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={() => submitReject(req.id)}
                            disabled={busyId === req.id}
                            data-testid={`request-reject-confirm-${req.id}`}>
                      Confirmer le refus
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejectingId(null)}>
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMadrassaRequestsPage;
